import { z } from 'zod';
import { precisionSchema, statutSchema } from './commun';

/**
 * Schéma de filtres commun à l'API (chaîne de requête) et à l'URL du front (TanStack Router).
 * Il accepte les valeurs répétées, les listes séparées par des virgules, les chaînes
 * "true"/"false" venant d'une requête HTTP et les valeurs déjà typées venant du routeur.
 */

function enListe(valeur: unknown): unknown {
  if (valeur === undefined || valeur === null || valeur === '') return undefined;
  const brut = Array.isArray(valeur) ? valeur : [valeur];
  const liste = brut.flatMap((v) => (typeof v === 'string' ? v.split(',') : [v])).filter((v) => v !== '');
  return liste.length > 0 ? liste : undefined;
}

function enBooleen(valeur: unknown): unknown {
  if (typeof valeur === 'string') return valeur === 'true' || valeur === '1';
  // Le routeur lit `?retard=1` comme le nombre 1 : même sens que la chaîne.
  if (typeof valeur === 'number') return valeur === 1;
  return valeur;
}

function enBoite(valeur: unknown): unknown {
  if (typeof valeur === 'string') return valeur.split(',').map(Number);
  return valeur;
}

const listeTexte = z.preprocess(enListe, z.array(z.string()).optional());
const texteOptionnel = z.preprocess((v) => (v === '' ? undefined : v), z.string().optional());

export const filtresSchema = z.object({
  mentor: texteOptionnel,
  consultant: texteOptionnel,
  statut: z.preprocess(enListe, z.array(statutSchema).optional()),
  type: listeTexte,
  pays: listeTexte,
  dep: listeTexte,
  precision: z.preprocess(enListe, z.array(precisionSchema).optional()),
  actifs: z.preprocess(enBooleen, z.boolean()).default(true),
  retard: z.preprocess(enBooleen, z.boolean()).default(false),
  bbox: z.preprocess(enBoite, z.tuple([z.number(), z.number(), z.number(), z.number()]).optional()),
});

export type Filtres = z.infer<typeof filtresSchema>;

/** Filtres par défaut : actifs seulement, rien d'autre. */
export const filtresParDefaut: Filtres = filtresSchema.parse({});

/** Sérialise des filtres en paramètres de requête, en omettant les valeurs par défaut. */
export function filtresVersParametres(filtres: Partial<Filtres>): URLSearchParams {
  const p = new URLSearchParams();
  if (filtres.mentor) p.set('mentor', filtres.mentor);
  if (filtres.consultant) p.set('consultant', filtres.consultant);
  for (const cle of ['statut', 'type', 'pays', 'dep', 'precision'] as const) {
    for (const valeur of filtres[cle] ?? []) p.append(cle, valeur);
  }
  if (filtres.actifs === false) p.set('actifs', 'false');
  if (filtres.retard) p.set('retard', 'true');
  if (filtres.bbox) p.set('bbox', filtres.bbox.join(','));
  return p;
}
